import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  MaterialUICore,
  MaterialIcons,
  Box,
} from '@iclinic/design-system';
import { RouteComponentProps } from 'react-router';

import {
  OffCanvasPlansComparationTable,
  MobileFooterSummary,
  ModalPaymentForm,
  PaymentSummary,
  Toggle,
  PlanSelect,
  ModalSelectPhysicians,
  ModalSuccess,
} from '../components';
import * as S from './styles';
import {
  fetchCheckoutData,
  setIsAnnual,
  toggleAnnualOrMonthlyPaymentRecurrence,
  toggleOffCanvasOpenClose,
  triggerVerifyInitialStep,
  updateSelectedPlan,
} from '../state/reducer';
import {
  getBaseProducts,
  isFetchingPlans,
  getMostSeller,
  isOffCanvasOpen,
  getSelectedAddons,
  getRecurrence,
  getSelectedPlan,
} from '../state/selectors';
import { Product } from '../services/types';
import ModalAddress from 'features/checkout/components/ModalAddressForm/ModalAddressForm';

const { PlaylistAddCheck } = MaterialIcons;

const { CircularProgress, useTheme, useMediaQuery } = MaterialUICore;

type PlansProps = RouteComponentProps;

export default function Plans({ location }: Readonly<PlansProps>): JSX.Element {
  const dispatch = useDispatch();

  const isFetching = useSelector(isFetchingPlans);
  const plans = useSelector(getBaseProducts);
  const selectedAddons = useSelector(getSelectedAddons);
  const selectedPlan = useSelector(getSelectedPlan);
  const mostSeller = useSelector(getMostSeller);
  const isAnnual = useSelector(getRecurrence);
  const isCanvasOpen = useSelector(isOffCanvasOpen);

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const queryParams = new URLSearchParams(location.search);

  const selectedPlanSlug = queryParams.get('selected_plan');

  const recurrence = queryParams.get('recurrence');

  useEffect(() => {
    dispatch(fetchCheckoutData());
  }, [dispatch]);

  useEffect(() => {
    if (plans.length > 0) {
      if (selectedPlanSlug) {
        const plan = plans.find((p) => p.slug === selectedPlanSlug);
        if (plan) {
          dispatch(updateSelectedPlan(plan));
        }
      }

      if (recurrence) {
        dispatch(setIsAnnual(recurrence === 'annual'));
      }
    }
  }, [dispatch, plans, recurrence, selectedPlanSlug]);

  const toggleRecurrence = () => {
    dispatch(toggleAnnualOrMonthlyPaymentRecurrence());
    const url = new URL(window.location.href);
    url.searchParams.set('recurrence', isAnnual ? 'monthly' : 'annual');
    window.history.pushState({}, '', url.toString());
  };

  const handleClick = () => {
    dispatch(toggleOffCanvasOpenClose());
  };

  const handlePaymentClick = () => {
    dispatch(triggerVerifyInitialStep());
  };

  if (isFetching)
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress data-testid="loading-plans" />
      </Box>
    );

  return (
    <S.Root>
      <S.ToggleHeading variant="md">
        {isMobile
          ? 'Qual plano você gostaria de assinar?'
          : 'Selecione o plano que melhor lhe atende'}
      </S.ToggleHeading>

      <S.ToggleContainer>
        <Toggle
          leftLabel="Mensal"
          rightLabel="Anual"
          value={isAnnual}
          onChange={toggleRecurrence}
        />

        <S.ComparationButton
          startIcon={
            <PlaylistAddCheck
              className={isCanvasOpen ? 'visible' : ''}
              fontSize="small"
            />
          }
          variant="text"
          color="transparent"
          onClick={handleClick}
        >
          Comparar funcionalidades dos planos
        </S.ComparationButton>
      </S.ToggleContainer>

      <S.FormContainer>
        {plans &&
          plans.map((plan: Product) => (
            <PlanSelect
              key={plan.id}
              plan={plan}
              isAnnual={isAnnual}
              mostSeller={mostSeller}
            />
          ))}
      </S.FormContainer>

      <S.SummaryWrapper>
        <PaymentSummary isAnnual={isAnnual} showSinglePrice />

        <Button
          fullWidth
          size="large"
          onClick={handlePaymentClick}
          disabled={!selectedPlan}
          data-ga="checkout_payment_modal_open_click"
        >
          Ir para pagamento
        </Button>
      </S.SummaryWrapper>

      <MobileFooterSummary
        isAnnual={isAnnual}
        onPaymentClick={handlePaymentClick}
      />

      <OffCanvasPlansComparationTable />

      {selectedAddons.map((addon) => (
        <ModalSelectPhysicians key={addon.slug} addon={addon} />
      ))}

      <ModalAddress />
      <ModalPaymentForm />
      <ModalSuccess />
    </S.Root>
  );
}
